import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Activity } from "lucide-react";

const CONSULT = ["Consultation Booked", "Consultation Completed", "Converted to Treatment"];
const COMPLETED = ["Consultation Completed", "Converted to Treatment"];
const CONVERTED = "Converted to Treatment";

function monthOptions() {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 12; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push({ key: `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`, label: m.toLocaleString("en-IN", { month: "long", year: "numeric" }) });
  }
  return out;
}

function summarize(list) {
  const enquiries = list.length;
  const booked = list.filter((p) => CONSULT.includes(p.enquiry_status)).length;
  const completed = list.filter((p) => COMPLETED.includes(p.enquiry_status)).length;
  const converted = list.filter((p) => p.enquiry_status === CONVERTED).length;
  const revenue = list.reduce((a, p) => a + (Number(p.revenue) || 0), 0);
  return { enquiries, booked, completed, converted, revenue, rate: enquiries ? Math.round((converted / enquiries) * 100) : 0 };
}

const pct = (a, b) => (b ? Math.round((a / b) * 100) + "%" : "—");
const delta = (cur, prev) => (prev ? `${cur >= prev ? "+" : ""}${Math.round(((cur - prev) / prev) * 100)}%` : cur ? "new" : "—");

export default function Report() {
  const { user } = useAuth();
  const months = useMemo(monthOptions, []);
  const [month, setMonth] = useState(months[0].key);
  const { data: patients = [], isLoading } = useQuery({ queryKey: ["patients"], queryFn: async () => (await http.get("/patients")).data });

  const prevKey = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 2, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, [month]);

  const cur = useMemo(() => patients.filter((p) => p.enquiry_date?.startsWith(month)), [patients, month]);
  const prev = useMemo(() => patients.filter((p) => p.enquiry_date?.startsWith(prevKey)), [patients, prevKey]);
  const s = useMemo(() => summarize(cur), [cur]);
  const ps = useMemo(() => summarize(prev), [prev]);

  const bySource = useMemo(() => {
    const m = new Map();
    for (const p of cur) {
      const row = m.get(p.acquisition_source) || { name: p.acquisition_source, enquiries: 0, converted: 0, revenue: 0 };
      row.enquiries++;
      if (p.enquiry_status === CONVERTED) row.converted++;
      row.revenue += Number(p.revenue) || 0;
      m.set(p.acquisition_source, row);
    }
    return [...m.values()].sort((a, b) => b.enquiries - a.enquiries);
  }, [cur]);

  const label = months.find((m) => m.key === month)?.label;

  return (
    <div className="space-y-6" data-testid="report-page">
      <div className="no-print flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-zinc-900">Monthly Report</h1>
          <p className="text-sm text-zinc-500 mt-1">Pick a month, then print or save as PDF.</p>
        </div>
        <div className="flex gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-52" data-testid="report-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{months.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => window.print()} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="print-report-btn">
            <Printer className="h-4 w-4" /> Print / Save PDF
          </Button>
        </div>
      </div>

      {isLoading ? <div className="text-zinc-500">Loading...</div> : (
        <div className="print-page bg-white border border-zinc-200 rounded-lg p-6 sm:p-10 max-w-4xl print:border-0 print:max-w-none" data-testid="report-sheet">
          <div className="flex items-start justify-between border-b border-zinc-200 pb-5">
            <div>
              <div className="flex items-center gap-2 text-emerald-700 text-xs font-semibold uppercase tracking-widest"><Activity className="h-4 w-4" /> PatientFlow</div>
              <h2 className="font-heading text-2xl font-semibold text-zinc-900 mt-2">Patient Acquisition Report</h2>
              <p className="text-sm text-zinc-600 mt-1" data-testid="report-month-label">{label}</p>
            </div>
            <div className="text-right text-sm text-zinc-600">
              <div className="font-medium text-zinc-900">{user?.clinic_name}</div>
              <div>{user?.physio_name}</div>
              <div>{user?.city}</div>
            </div>
          </div>

          <Section title="Summary">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Kpi label="Enquiries" value={s.enquiries} sub={`${delta(s.enquiries, ps.enquiries)} vs prev. month`} />
              <Kpi label="Consultations" value={s.completed} sub={`${delta(s.completed, ps.completed)} vs prev. month`} />
              <Kpi label="New Patients" value={s.converted} sub={`${delta(s.converted, ps.converted)} vs prev. month`} />
              <Kpi label="Conversion Rate" value={`${s.rate}%`} sub={`prev. ${ps.rate}%`} />
              <Kpi label="Revenue" value={`₹ ${s.revenue.toLocaleString("en-IN")}`} sub={`${delta(s.revenue, ps.revenue)} vs prev. month`} />
              <Kpi label="Avg / Patient" value={`₹ ${(s.converted ? Math.round(s.revenue / s.converted) : 0).toLocaleString("en-IN")}`} />
            </div>
          </Section>

          <Section title="Acquisition Funnel">
            <table className="w-full text-sm">
              <tbody>
                <Row k="Enquiries" v={s.enquiries} />
                <Row k="Consultations Booked" v={s.booked} extra={pct(s.booked, s.enquiries)} />
                <Row k="Consultations Completed" v={s.completed} extra={pct(s.completed, s.booked)} />
                <Row k="Converted to Treatment" v={s.converted} extra={pct(s.converted, s.completed)} bold />
              </tbody>
            </table>
          </Section>

          <Section title="Sources">
            {bySource.length === 0 ? <p className="text-sm text-zinc-500">No enquiries recorded in {label}.</p> : (
              <table className="w-full text-sm" data-testid="report-sources-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                    <th className="py-2 font-semibold">Source</th>
                    <th className="py-2 font-semibold text-right">Enquiries</th>
                    <th className="py-2 font-semibold text-right">Converted</th>
                    <th className="py-2 font-semibold text-right">Conv. %</th>
                    <th className="py-2 font-semibold text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {bySource.map((r) => (
                    <tr key={r.name} className="border-b border-zinc-100">
                      <td className="py-2 text-zinc-900">{r.name}</td>
                      <td className="py-2 text-right">{r.enquiries}</td>
                      <td className="py-2 text-right">{r.converted}</td>
                      <td className="py-2 text-right">{pct(r.converted, r.enquiries)}</td>
                      <td className="py-2 text-right">₹ {r.revenue.toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <p className="mt-8 pt-4 border-t border-zinc-200 text-[11px] text-zinc-400">
            Generated by PatientFlow on {new Date().toLocaleDateString("en-IN")}. Contains aggregate acquisition data only — no medical information.
          </p>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-7">
      <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-zinc-700 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="border border-zinc-200 rounded-md p-3">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-heading text-xl font-semibold text-zinc-900 mt-1">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Row({ k, v, extra, bold }) {
  return (
    <tr className="border-b border-zinc-100">
      <td className={`py-2 ${bold ? "font-semibold text-zinc-900" : "text-zinc-700"}`}>{k}</td>
      <td className={`py-2 text-right ${bold ? "font-semibold" : ""}`}>{v}</td>
      <td className="py-2 text-right text-zinc-500 w-20">{extra || ""}</td>
    </tr>
  );
}
